import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { temEscopo } from 'dashboard/helper/escopos';

/**
 * ACESSO-01 (L8a): a decisão da tela pelo escopo `módulo:ação`, com os escopos efetivos da pessoa na conta atual
 * (store `acesso`, que os tira do perfil).
 *
 * - `ligado`: a chave `ACESSO_GRANULAR` está ligada.
 * - `pode(escopo)`: o escopo, ou a lista em que basta um, está nos escopos da pessoa. O super admin pode tudo.
 * - `decide(escopo, hoje)`: com a chave ligada, `pode(escopo)`; desligada, `hoje()`, o cheque de hoje
 *   (`usePolicy`, `usePermissions`, `useAdmin`...).
 *
 * Com a chave desligada, `pode` não decide nada: o servidor ainda segue as regras de hoje, e a tela também. Quem chama
 * faz `ligado ? pode(escopo) : <cheque de hoje>`, que é o `decide`.
 *
 * @returns {{
 *   ligado: import('vue').ComputedRef<boolean>,
 *   pode: (escopo: string | string[]) => boolean,
 *   decide: (escopo: string | string[], hoje: () => boolean) => boolean,
 * }}
 */
export function useAcesso() {
  // ACESSO-01 (L8b): só o `true` liga a chave. Qualquer outra coisa (a loja sem o módulo `acesso`, ou um dublê de teste
  // que responde a todo getter) vale desligada, e a tela fica como hoje.
  const naLoja = useMapGetter('acesso/ligado');
  const ligado = computed(() => naLoja.value === true);
  const escopos = useMapGetter('acesso/escopos');
  const usuario = useMapGetter('getCurrentUser');

  // A regra é a de fora de componente (`helper/escopos`), a do guard das rotas.
  const pode = escopo =>
    temEscopo(escopo, escopos.value, usuario.value?.type === 'SuperAdmin');
  const decide = (escopo, hoje) => (ligado.value ? pode(escopo) : hoje());

  return { ligado, pode, decide };
}
